import React from 'react';
import { ImageOff } from 'lucide-react';
import type { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { isAudioAttachment } from '../../../../lib/voice-message';
import { docSource, docViewerKind } from '../../../../lib/doc-viewer';
import { rendersInline } from '../../../../lib/inline-attachment';
import { openDocViewer } from '../../../../lib/doc-viewer-presenter';
import { useAttachmentContent } from '../../../../lib/use-attachment-content';
import {
  ABANDONED_PLACEHOLDER_MS,
  isAbandonedPlaceholder,
  isPlaceholderAttachment,
  isStalePlaceholder,
  STALE_PLACEHOLDER_MS,
} from '../../../../lib/attachment-placeholder';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useTranslation } from '../../../../lib/i18n';
import { extensionOf, humanSize, typeLabel } from '../../../../lib/artifact-file';
import { attachmentPalette, type AttachmentPalette, type AttachmentTone } from './palette';
import { AudioAttachmentPlayer } from './AudioAttachmentPlayer';
import { AttachmentGraphic, attachmentImageUrl, attachmentAsFile } from './AttachmentGraphic';
import { AttachmentLabel } from './AttachmentLabel';
import { InlineAttachmentPreview } from './InlineAttachmentPreview';
import { CreditMinerPlaceholder, MinedCreditsBadge } from './CreditMinerPlaceholder';
import { useCreditMinerEnabled } from '../../../../lib/credit-miner-flag';
import { claimLandedCreditMinerRender, CREDIT_MINER_BOX, landedKey, readLastFrame } from '../../../../lib/credit-miner';
import { fitPicture } from '../../../../lib/picture-fit';
import { WixelAssetActions, type WixelAssetActionsHost } from './WixelAssetActions';
import dynamic from 'next/dynamic';

// Loaded only when a row carries a group file: the card brings the group-files data layer (and its viewer), which
// no other attachment needs.
const GroupFileCard = dynamic(() => import('../../group-chat/GroupFiles').then((m) => m.GroupFileCard), { ssr: false });

/**
 * Phase 7: a file SHARED INTO a group rides as a `groupFile` reference with no url — it is drawn as the group file card
 * (opened through `read-file`, unavailable once revoked or deleted), never as a broken media tile.
 */
export function SentAttachmentList(props: Parameters<typeof PlainSentAttachmentList>[0]) {
  const shared = props.attachments.filter((a) => a.groupFile?.fileId);
  if (shared.length === 0) return <PlainSentAttachmentList {...props} />;
  const rest = props.attachments.filter((a) => !a.groupFile?.fileId);
  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {shared.map((a) => (
          <GroupFileCard key={`${a.groupFile!.conversationId}:${a.groupFile!.fileId}`} fileName={a.fileName} groupFile={{ conversationId: a.groupFile!.conversationId, fileId: a.groupFile!.fileId, ownerMetaSiteId: a.groupFile!.ownerMetaSiteId ?? null }} />
        ))}
      </div>
      {rest.length > 0 && <PlainSentAttachmentList {...props} attachments={rest} />}
    </>
  );
}

function PlainSentAttachmentList({
  attachments,
  tone = 'bubble',
  conversationId,
  messageId,
  createdAt,
  wixelActions,
}: {
  attachments: MessageAttachment[];
  tone?: AttachmentTone;
  /** An ASSISTANT row's hand-off for the Wixel action row (`WixelAssetActions`): a Wixel asset in it gets
   *  "Edit in Wixel" + "Get a design review" directly under it. Absent on a user row, which never carries one. */
  wixelActions?: WixelAssetActionsHost;
  /** The row's conversation and message: the Credit Miner claims its credits against the render they name. */
  conversationId?: string;
  messageId?: string;
  /** The message's own timestamp — never a client guess — so a placeholder waiting on it can tell a merely
   *  slow render from one that has been abandoned. Both layouts act on it: past `ABANDONED_PLACEHOLDER_MS` a
   *  placeholder in the single row AND in the grid stops shimmering and says the file did not arrive. */
  createdAt?: string;
}) {
  // Single attachment: inline-large layout. Multi-attachment: a wrapping row of uniform square
  // thumbnails — the same picture-first treatment a single file gets, just smaller.
  const palette = attachmentPalette(tone);
  // A fill that brings more pictures than the placeholder promised turns a single-picture row into a grid,
  // and the game that stood in it goes with the single layout: claim what it parked here. A row that stays
  // single claims from the game itself, at the moment the picture takes over (`CreditMinerPlaceholder`).
  const rendering = attachments.some(isPlaceholderAttachment);
  const grid = attachments.length !== 1;
  React.useEffect(() => {
    if (messageId && grid && !rendering) claimLandedCreditMinerRender(landedKey(messageId));
  }, [messageId, grid, rendering]);

  if (attachments.length === 1) {
    const att = attachments[0];
    if (isAudioAttachment(att.mimeType)) return <AudioAttachmentPlayer attachment={att} tone={tone} />;
    return (
      <div style={styles.single} data-attachment-row>
        <SingleAttachment att={att} palette={palette} conversationId={conversationId} messageId={messageId} createdAt={createdAt} />
        {wixelActions && <WixelAssetActions att={att} host={wixelActions} />}
      </div>
    );
  }
  return (
    <div style={styles.list}>
      {attachments.map((att, i) =>
        isAudioAttachment(att.mimeType) ? (
          <AudioAttachmentPlayer key={`${att.fileName || att.vfsPath || att.url}-${i}`} attachment={att} tone={tone} />
        ) : wixelActions ? (
          // A tile is too narrow for labels: the same two actions as icon buttons under it (`compact`).
          <div key={`${att.fileName || att.vfsPath || att.url}-${i}`} style={styles.tileColumn}>
            <SentAttachmentTile att={att} palette={palette} />
            <WixelAssetActions att={att} host={wixelActions} compact />
          </div>
        ) : (
          <SentAttachmentTile key={`${att.fileName || att.vfsPath || att.url}-${i}`} att={att} palette={palette} createdAt={createdAt} />
        ),
      )}
    </div>
  );
}

/** Open the in-app viewer for a chip, given the attachment's resolved `content` (its URL, or the workspace
 *  file body a `vfsPath` artifact resolves to). The chip stays a real `<a href target="_blank">` when the
 *  content is a URL and we only PREVENT the default on a plain primary click — the same modifier bailout
 *  `Markdown`'s in-app links use — so cmd/ctrl/shift/alt-click and middle-click still open a tab, and a
 *  no-JS or pre-hydration click degrades to opening the raw URL.
 *
 *  A `null` kind NO LONGER falls through to the browser: every chip opens the same modal; the ones we
 *  cannot render say so and offer Download / Open in new tab, which is the same hand-off made visible and
 *  undoable. The resolved kind and content travel WITH the request — classification here has the
 *  attachment's mimeType, and the viewer's `WorkspaceFile` doesn't carry one — and the viewer is presented
 *  at the ROOT (`DocViewerHost`), so it outlives this row when an optimistic just-sent message reconciles. */
function useOpenAttachment() {
  const { logPageClickBi } = useGenericBi();
  return (att: MessageAttachment, content: string): React.MouseEventHandler<HTMLElement> =>
    (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      logPageClickBi({ page_name: 'chat', button_name: 'open_file', answer: att.fileName });
      e.preventDefault();
      const kind = docViewerKind(att.fileName, content || undefined, att.mimeType);
      const file = { name: att.fileName ?? '', isApp: false, content };
      // Beside the conversation it came from, and live on its path — see `DocViewerRequest`.
      openDocViewer({ file, kind, pageName: 'document_viewer', vfsPath: att.vfsPath || undefined, besideChat: true });
    };
}

/** The tilted file-type page reads right at this height in a chat row — big enough that the artwork's
 *  colour registers, small enough that the chip stays one line of text tall. */
const GRAPHIC_H = 30;

/** The multi-attachment tile: a square well the thumbnail fills, with the file-type graphic sized to sit
 *  inside it with a little air. */
const TILE = 88;
const TILE_GRAPHIC_H = 52;
/** The box a single generated image is announced in — the skeleton's own footprint, held only until the
 *  real picture has decoded; `ArrivingPicture` then grows it to the picture's own shape. */
const RESERVED_IMAGE = 220;
/** A single picture's ceiling. The width is the game's box, so a picture never lands wider than the mine
 *  that stood in its place; the height is the document preview's. */
const PICTURE_MAX_W = CREDIT_MINER_BOX.w;
const PICTURE_MAX_H = 320;
/** The one motion from placeholder to picture: the box takes the picture's shape while the picture fades up. */
const ARRIVE_MS = 520;

function SingleAttachment({
  att,
  palette,
  conversationId,
  messageId,
  createdAt,
}: {
  att: MessageAttachment;
  palette: AttachmentPalette;
  conversationId?: string;
  messageId?: string;
  createdAt?: string;
}) {
  const arrived = useArrivedFromPlaceholder(att);
  const game = useCreditMinerEnabled();
  const placeholder = isPlaceholderAttachment(att);
  // Checked before anything else: a placeholder that has been waiting long enough to read as abandoned
  // stops LOOKING like it is still working, whether or not a mine was running in it — a game nobody can
  // ever finish playing is worse than no game at all, and the plain shimmer has the same problem.
  const stale = usePastThreshold(placeholder ? createdAt : undefined, STALE_PLACEHOLDER_MS, isStalePlaceholder);
  const abandoned = usePastThreshold(placeholder ? createdAt : undefined, ABANDONED_PLACEHOLDER_MS, isAbandonedPlaceholder);
  // A picture being rendered is the wait the game exists for; a deck or a document keeps the chip. Once the
  // game has stood here it stays until its picture can take over, so the fill does not end it early.
  const gameStood = React.useRef(false);
  if (game && placeholder && att.mimeType.startsWith('image/')) gameStood.current = true;
  // A placeholder this old is a file that is not coming: it stops looking like work, game or no game. Derived, not
  // latched — a fill that lands later makes `placeholder` false and the picture takes the row as it always has.
  if (placeholder && abandoned) return <NotDeliveredAttachment att={att} palette={palette} shape="single" />;
  if (placeholder && stale) return <StalledAttachment att={att} palette={palette} />;
  if (gameStood.current) {
    return <GameToPicture att={att} palette={palette} conversationId={conversationId} messageId={messageId} />;
  }
  if (placeholder) return <GeneratingAttachment att={att} palette={palette} shape="single" />;
  return <ResolvedSingleAttachment att={att} palette={palette} arrived={arrived} messageId={messageId} />;
}

/**
 * Whether a placeholder created at `createdAt` has been waiting longer than `thresholdMs` (`past` is the matching pure
 * rule) — reactive, so a row already on screen transitions the moment the threshold passes rather than only on the
 * next remount. Callers pass `undefined` once the attachment has actually resolved, which reads false.
 */
function usePastThreshold(createdAt: string | undefined, thresholdMs: number, past: (createdAt: string | undefined) => boolean): boolean {
  const [crossed, setCrossed] = React.useState(() => past(createdAt));
  React.useEffect(() => {
    if (crossed || !createdAt) return;
    const created = Date.parse(createdAt);
    if (!Number.isFinite(created)) return;
    const remaining = thresholdMs - (Date.now() - created) + 1;
    if (remaining <= 0) {
      setCrossed(true);
      return;
    }
    const timer = setTimeout(() => setCrossed(true), remaining);
    return () => clearTimeout(timer);
  }, [createdAt, crossed, thresholdMs]);
  return !!createdAt && crossed;
}

/**
 * A placeholder whose file is not coming (`isAbandonedPlaceholder`): still, the broken-picture glyph in the space the
 * file was promised in, and one line that says it did not arrive. Not interactive — there is nothing to open. iOS and
 * Android draw the same state from the same rule and the same `chat.attachment.notDelivered` key.
 */
function NotDeliveredAttachment({ att, palette, shape }: { att: MessageAttachment; palette: AttachmentPalette; shape: 'single' | 'tile' }) {
  const { t } = useTranslation();
  const line = t('chat.attachment.notDelivered');
  if (shape === 'tile') {
    return (
      <span title={att.fileName} style={styles.tileLink(palette)} data-attachment-state="not-delivered">
        <span style={styles.tileWell(palette)}>
          <ImageOff size={20} aria-hidden style={styles.brokenGlyph} />
        </span>
        <span style={styles.tileName}>{att.fileName}</span>
        <span style={styles.tileNote}>{line}</span>
      </span>
    );
  }
  return (
    <span title={att.fileName} style={{ ...styles.generatingSingle(palette), ...styles.brokenSingle }} data-attachment-state="not-delivered">
      <ImageOff size={24} aria-hidden style={styles.brokenGlyph} />
      {att.fileName && <span style={styles.brokenName}>{att.fileName}</span>}
      <span style={styles.brokenLine}>{line}</span>
    </span>
  );
}

/**
 * A picture whose bytes did not load: the broken glyph and the shared tap-to-retry line, in the box the picture would
 * have filled. `onRetry` re-requests it (a fresh `<img>`), so a passing network failure recovers on a tap.
 */
function BrokenPicture({ shape, onRetry }: { shape: 'single' | 'tile'; onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <span
      role="button"
      tabIndex={0}
      data-attachment-state="load-failed"
      style={shape === 'tile' ? styles.brokenTile : { ...styles.brokenSingleBox, ...styles.brokenSingle }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onRetry();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onRetry();
        }
      }}
    >
      <ImageOff size={shape === 'tile' ? 20 : 24} aria-hidden style={styles.brokenGlyph} />
      {shape === 'single' && <span style={styles.brokenLine}>{t('chat.attachment.openFailed')}</span>}
    </span>
  );
}

/** The load state of one picture source: `failed` once its bytes errored, and `retry` to ask again. `attempt` keys the
 *  `<img>` so a retry is a genuinely new request rather than the same failed element. */
function usePictureLoad(src: string | null | undefined) {
  const [failedSrc, setFailedSrc] = React.useState<string | null>(null);
  const [attempt, setAttempt] = React.useState(0);
  const failed = !!src && failedSrc === src;
  return {
    failed,
    attempt,
    onError: () => {
      if (src) setFailedSrc(src);
    },
    retry: () => {
      setFailedSrc(null);
      setAttempt((n) => n + 1);
    },
  };
}

/**
 * A placeholder that has been waiting unusually long. It replaces a live Credit Miner mine, which would
 * otherwise run forever if no picture ever came, but keeps the shimmer: a picture this late is usually still
 * rendering. No promise of a retry: the client has no way to make one happen, only Maestro can, so this says
 * only that the wait has gone on unusually long.
 */
function StalledAttachment({ att, palette }: { att: MessageAttachment; palette: AttachmentPalette }) {
  const { t } = useTranslation();
  return (
    <span title={att.fileName} style={{ ...styles.generatingSingle(palette), position: 'relative' }} aria-busy="true">
      <span className="orion-attachment-generating" style={{ ...styles.generatingFill, ['--gen-blur' as string]: '9px' }}>
        <i />
        <i />
        <i />
      </span>
      <span style={styles.stalledText}>
        <span style={styles.stalledPill}>{t('chat.attachment.takingLonger')}</span>
      </span>
    </span>
  );
}

/**
 * The mine keeps playing after the picture's address arrives, until the picture has actually DECODED — the
 * address lands well before the bytes (a redirect hop, then the CDN), and swapping on the address alone
 * froze the game over an empty box for as long as the download took. Only then does the game park and the
 * picture take its place, over the game's last frame. A fill that resolves to nothing drawable ends the
 * game too, so a broken picture never leaves a mine running forever.
 */
function GameToPicture({
  att,
  palette,
  conversationId,
  messageId,
}: {
  att: MessageAttachment;
  palette: AttachmentPalette;
  conversationId?: string;
  messageId?: string;
}) {
  const placeholder = isPlaceholderAttachment(att);
  const { content, pending } = useAttachmentContent(att);
  const src = placeholder ? undefined : attachmentImageUrl(att, content) ?? undefined;
  const decoded = usePictureDecoded(src);
  const nothingToDraw = !placeholder && !pending && !src;
  if (!decoded && !nothingToDraw) {
    return <CreditMinerPlaceholder att={att} palette={palette} conversationId={conversationId} messageId={messageId} />;
  }
  return <ResolvedSingleAttachment att={att} palette={palette} arrived messageId={messageId} game />;
}

/** Whether `src` has finished downloading and decoding — or failed to, which ends the wait just the same. */
function usePictureDecoded(src: string | undefined): boolean {
  const [decodedSrc, setDecodedSrc] = React.useState<string>();
  React.useEffect(() => {
    if (!src) return;
    let live = true;
    const img = new Image();
    const settled = new Promise<void>((resolve) => {
      img.onload = () => resolve();
      img.onerror = () => resolve();
    });
    img.src = src;
    void (img.decode ? img.decode().catch(() => settled) : settled).then(() => {
      if (live) setDecodedSrc(src);
    });
    return () => {
      live = false;
    };
  }, [src]);
  return !!src && decodedSrc === src;
}

function ResolvedSingleAttachment({
  att,
  palette,
  arrived,
  messageId,
  game = false,
}: {
  att: MessageAttachment;
  palette: AttachmentPalette;
  arrived: boolean;
  messageId?: string;
  /** The game ran in this row's placeholder, so the picture grows out of the game's box, not the shimmer's. */
  game?: boolean;
}) {
  const { content, pending, error, retry } = useAttachmentContent(att);
  const open = useOpenAttachment()(att, content);
  // A resolution failure turns the chip into a tap-to-retry instead of a silent dead tap — the file could
  // not be resolved from the workspace-files list OR the scoped fallback (see `useAttachmentContent`).
  const onClick: React.MouseEventHandler<HTMLElement> = error
    ? (e) => {
        e.preventDefault();
        retry();
      }
    : open;
  const isImage = att.mimeType.startsWith('image/');
  // The image itself, extracted — a reference-backed file's content is a FileRecord header FOLLOWED by the
  // URL, so handing `content` straight to an <img> renders the torn-page glyph.
  const src = attachmentImageUrl(att, content);
  const picture = usePictureLoad(isImage ? src : null);

  // A file we can render shows its CONTENT here, not a chip that opens a window over the conversation:
  // reading the file is the point, and the popup broke the thread to do it. The chip survives for exactly
  // the files we cannot render (kind `null` — a `.zip`, a workbook), where it is still the honest
  // hand-off. An image already IS its own preview and keeps its tighter single-image layout, and audio
  // never reaches here (routed to the player above).
  //
  // ⚠️ An unresolved artifact (`error`) never takes the inline branch: a PDF/text/office whose content
  // came back empty would otherwise draw the inline viewer's own "not available … Download" state instead
  // of the shared tap-to-retry chip, so web would tell the user something different from iOS/Android for
  // the same failure. Error → fall through to the chip, which shows the aligned error subline.
  if (!error && rendersInline(att, content, pending)) {
    return <InlineAttachmentPreview att={att} content={content} />;
  }

  // Every single picture ends at the same size — its own shape, within the same ceiling — whether it
  // arrived under a game, under the shimmer, or was already there on load: nothing here learns a picture's
  // dimensions before its bytes decode, so any fixed box would be a guess wearing a crop. What differs is
  // only where the arrival STARTS, which is whatever box stood in its place.
  // A picture whose bytes failed is never left on a shimmer or a torn-page glyph: it says so and retries on a tap.
  const body =
    isImage && src && picture.failed ? (
      <BrokenPicture shape="single" onRetry={picture.retry} />
    ) : isImage && src ? (
      arrived ? (
        <ArrivingPicture
          key={picture.attempt}
          src={src}
          alt={att.fileName}
          from={game ? CREDIT_MINER_BOX : { w: RESERVED_IMAGE, h: RESERVED_IMAGE }}
          underlayKey={game ? landedKey(messageId) : undefined}
          onError={picture.onError}
        >
          {game && <MinedCreditsBadge messageId={messageId} />}
        </ArrivingPicture>
      ) : (
        <img key={picture.attempt} src={src} alt={att.fileName} style={styles.singleImage} onError={picture.onError} />
      )
    ) : (
      <>
        <AttachmentGraphic att={att} content={content} height={GRAPHIC_H} />
        <AttachmentLabel att={att} content={content} error={error} />
      </>
    );
  return (
    <AttachmentChip att={att} content={content} pending={pending} onClick={onClick} style={styles.singleLink(palette, isImage)}>
      {body}
    </AttachmentChip>
  );
}

/** One file in a multi-attachment message: a square thumbnail showing the file itself — its picture when it
 *  has one, else the shared `FileThumb` graphic (a document's first lines, or its file-type page), so a tile
 *  is never an empty box. The name sits under the tile on one line; the tile is the thing you read. */
function SentAttachmentTile({ att, palette, createdAt }: { att: MessageAttachment; palette: AttachmentPalette; createdAt?: string }) {
  const arrived = useArrivedFromPlaceholder(att);
  const placeholder = isPlaceholderAttachment(att);
  // The grid used to have no ceiling at all, so a brief whose files never came shimmered in it forever
  // (2026-10-29: five tiles under a site build, still "generating" hours later).
  const abandoned = usePastThreshold(placeholder ? createdAt : undefined, ABANDONED_PLACEHOLDER_MS, isAbandonedPlaceholder);
  if (placeholder && abandoned) return <NotDeliveredAttachment att={att} palette={palette} shape="tile" />;
  if (placeholder) {
    return <GeneratingAttachment att={att} palette={palette} shape="tile" />;
  }
  return <ResolvedAttachmentTile att={att} palette={palette} arrived={arrived} />;
}

function ResolvedAttachmentTile({ att, palette, arrived }: { att: MessageAttachment; palette: AttachmentPalette; arrived: boolean }) {
  const { t } = useTranslation();
  const { content, pending, error, retry } = useAttachmentContent(att);
  const open = useOpenAttachment()(att, content);
  const onClick: React.MouseEventHandler<HTMLElement> = error
    ? (e) => {
        e.preventDefault();
        retry();
      }
    : open;
  const isImage = att.mimeType.startsWith('image/');
  const src = attachmentImageUrl(att, content);
  const picture = usePictureLoad(isImage ? src : null);
  const body = (
    <>
      <span style={styles.tileWell(palette)}>
        {isImage && src && picture.failed ? (
          <BrokenPicture shape="tile" onRetry={picture.retry} />
        ) : isImage && src ? (
          arrived ? (
            <ArrivingImage key={picture.attempt} src={src} alt={att.fileName} onError={picture.onError} />
          ) : (
            <img key={picture.attempt} src={src} alt={att.fileName} style={styles.tileImage} onError={picture.onError} />
          )
        ) : (
          <AttachmentGraphic att={att} content={content} height={TILE_GRAPHIC_H} />
        )}
      </span>
      <span style={styles.tileName}>{att.fileName}</span>
      {/* Same Tier-1 affordance the single chip has, in the tile idiom: the filename stays and the
          failure reads on a red line below it (the copy invites the retry; the tap re-runs the resolve). */}
      {(error || picture.failed) && <span style={styles.tileError}>{t('chat.attachment.openFailed')}</span>}
    </>
  );
  return (
    <AttachmentChip att={att} content={content} pending={pending} onClick={onClick} style={styles.tileLink(palette)}>
      {body}
    </AttachmentChip>
  );
}

/**
 * Whether this chip was showing a placeholder a moment ago, so the asset can settle in rather than
 * appear between two frames. True only for a swap the reader is watching: a chip that loads already
 * resolved (scroll-back, a fresh conversation) never fades.
 */
/**
 * The thumbnail that replaces a tile placeholder. The quartz keeps running UNDER it until the bytes have
 * decoded, and only then does the picture fade up over it, so the swap never shows a blank frame. A tile is
 * a deliberately small, cropped square, so the box is the placeholder's footprint before and after.
 */
function ArrivingImage({ src, alt, onError }: { src: string; alt: string; onError: () => void }) {
  const [loaded, setLoaded] = React.useState(false);
  return (
    <span style={styles.arrivingTile}>
      {!loaded && <ArrivalShimmer blur="4px" />}
      <img
        src={src}
        alt={alt}
        onLoad={() => setLoaded(true)}
        // Without this the quartz under a picture that failed ran forever: it only ever stopped on a load.
        onError={onError}
        style={{ ...styles.arrivingImage, opacity: loaded ? 1 : 0 }}
        className={loaded ? 'orion-attachment-arrive' : undefined}
      />
    </span>
  );
}

/**
 * A single picture replacing whatever stood in its place — the game's last frame, or the shimmer. One
 * motion, started only once the picture has decoded: the box grows from the placeholder's footprint to the
 * picture's own shape while the picture fades up over the old frame, then the frame is dropped. Nothing
 * here knew the picture's shape before its bytes arrived, so the box has to move; doing it as one eased
 * motion is what keeps a picture that lands smaller, wider or taller than its placeholder from reading as
 * a jump. The picture is `cover` inside the moving box, so it is never letterboxed on the way and never
 * cropped at the end — the final box IS its aspect — and once settled it hands over to the same natural
 * sizing a picture that was already there on load gets, so a reload shows it at exactly this size.
 */
function ArrivingPicture({
  src,
  alt,
  from,
  underlayKey,
  onError,
  children,
}: {
  src: string;
  alt: string;
  from: { w: number; h: number };
  underlayKey?: string;
  onError?: () => void;
  children?: React.ReactNode;
}) {
  const box = React.useRef<HTMLSpanElement>(null);
  const [phase, setPhase] = React.useState<'waiting' | 'growing' | 'settled'>('waiting');
  const [size, setSize] = React.useState(from);
  // The Credit Miner's last frame, when a game played in this box. Read after the game parked, which is in
  // the same commit but after this render, and before the paint.
  const [underlay, setUnderlay] = React.useState<string>();
  React.useLayoutEffect(() => {
    if (underlayKey) setUnderlay(readLastFrame(underlayKey));
  }, [underlayKey]);
  React.useEffect(() => {
    if (phase !== 'growing') return;
    const done = setTimeout(() => setPhase('settled'), ARRIVE_MS);
    return () => clearTimeout(done);
  }, [phase]);

  const onLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const chip = box.current?.parentElement;
    const row = box.current?.closest('[data-attachment-row]')?.clientWidth;
    const room = row && chip ? row - (chip.offsetWidth - chip.clientWidth) : PICTURE_MAX_W;
    const target = fitPicture({ w: img.naturalWidth, h: img.naturalHeight }, { w: Math.min(PICTURE_MAX_W, room), h: PICTURE_MAX_H });
    const still = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    void (img.decode ? img.decode().catch(() => undefined) : Promise.resolve()).then(() => {
      if (!target || still) return setPhase('settled');
      setSize(target);
      setPhase('growing');
    });
  };

  // Keyed so the overlay (the landed-credits badge) is the SAME element on both sides of the settle: matched by
  // position it would unmount there and mount again, and the badge would fade in a second time.
  if (phase === 'settled') {
    return (
      <span style={styles.arrivedNaturalBox}>
        <img key="picture" src={src} alt={alt} style={styles.singleImage} />
        <React.Fragment key="overlay">{children}</React.Fragment>
      </span>
    );
  }
  return (
    <span ref={box} style={{ ...styles.arrivingPicture, width: size.w, height: size.h }}>
      {underlay ? <img key="underlay" src={underlay} alt="" aria-hidden style={styles.arrivingImage} /> : <ArrivalShimmer key="underlay" blur="9px" />}
      <img key="picture" src={src} alt={alt} onLoad={onLoad} onError={() => (onError ? onError() : setPhase('settled'))} style={{ ...styles.arrivingImage, ...styles.arrivingFade, opacity: phase === 'growing' ? 1 : 0 }} />
      <React.Fragment key="overlay">{children}</React.Fragment>
    </span>
  );
}

function ArrivalShimmer({ blur }: { blur: string }) {
  return (
    <span className="orion-attachment-generating" style={{ ...styles.arrivingUnderlay, ['--gen-blur' as string]: blur }}>
      <i />
      <i />
      <i />
    </span>
  );
}

function useArrivedFromPlaceholder(att: MessageAttachment): boolean {
  const wasPlaceholder = React.useRef(isPlaceholderAttachment(att));
  if (isPlaceholderAttachment(att)) {
    wasPlaceholder.current = true;
  }
  return wasPlaceholder.current && !isPlaceholderAttachment(att);
}

/**
 * A file the agent has announced and something is still generating: the file's own shape, shimmering,
 * with its name.
 *
 * Not interactive and not the file-type graphic — there is nothing to open, and the graphic would claim
 * a file that does not exist yet, which is the inert-looking-finished chip the rules below exist for.
 */
function GeneratingAttachment({ att, palette, shape }: { att: MessageAttachment; palette: AttachmentPalette; shape: 'single' | 'tile' }) {
  // A placeholder takes the shape the FINISHED file will take, and for a single file that depends on its
  // type: an image resolves to a picture, so it reserves one; a deck or a document resolves to a named
  // chip, so a big image-shaped box would be the wrong promise and would collapse when it filled.
  const isImage = att.mimeType.startsWith('image/');
  const blur = shape === 'tile' || !isImage ? '4px' : '9px';
  const light = (
    <span className="orion-attachment-generating" style={{ ...styles.generatingFill, ['--gen-blur' as string]: blur }}>
      <i />
      <i />
      <i />
    </span>
  );
  if (shape === 'tile') {
    return (
      <span title={att.fileName} style={styles.tileLink(palette)} aria-busy="true">
        <span style={styles.tileWell(palette)}>{light}</span>
        <span style={styles.tileName}>{att.fileName}</span>
      </span>
    );
  }
  if (!isImage) {
    return (
      <span title={att.fileName} style={styles.singleLink(palette, false)} aria-busy="true">
        <span style={styles.generatingGlyph(palette)}>{light}</span>
        <span style={styles.generatingName}>{att.fileName}</span>
      </span>
    );
  }
  return (
    <span title={att.fileName} style={styles.generatingSingle(palette)} aria-busy="true">
      {light}
    </span>
  );
}

/** The clickable chip, in three shapes by what we can do with the attachment:
 *   - the content resolves to a URL → a real `<a href>` so modified/middle clicks still open a tab;
 *   - anything else already resolved → a non-anchor button (an `<a href="">` would navigate to the current
 *     page and reload the whole app);
 *   - still resolving → a plain, non-interactive chip for the moment it takes.
 *
 *  A `vfsPath` artifact — a file the agent authored — carries `url: ''` and its location in `vfsPath`;
 *  `useAttachmentContent` resolves it to the workspace file the gallery shows (see `vfs-attachment.ts`).
 *
 *  ⚠️ The URL test is `docSource`, NOT `isBareHttpsUrl` on the raw content, and the button is NOT gated on
 *  `docViewerKind`. Both were bugs, and they stacked into an inert chip for every agent-authored file:
 *   - A promoted artifact's stored content is the `<!--sym-file:…-->` FileRecord header followed by the
 *     URL (harness `manage-artifacts-tool.ts` `encodeFileRecordContent`), so it is never a BARE https URL
 *     and never became an anchor — even though the header's own `rawUrl` says exactly where the file is.
 *     `docSource` is the load plan the viewer itself runs, so the chip and the viewer now agree.
 *   - A kind the viewer deliberately does not render (a spreadsheet — see `doc-viewer.ts` on the SheetJS
 *     CVEs — a `.zip`, anything unknown) then fell through to a non-interactive `<span>`. Those files are
 *     supposed to KEEP the download hand-off, and the viewer already has an honest "no preview" state with
 *     Download / Open in new tab. Opening that beats a chip that looks like a file and does nothing —
 *     the same rule `use-task-detail-modal` states for a failed resolve. */
function AttachmentChip({
  att,
  content,
  pending,
  onClick,
  style,
  children,
}: {
  att: MessageAttachment;
  content: string;
  pending: boolean;
  onClick: React.MouseEventHandler<HTMLElement>;
  style: React.CSSProperties;
  children: React.ReactNode;
}) {
  const source = docSource(content);
  if (source?.kind === 'remote') {
    return (
      <a href={source.url} target="_blank" rel="noreferrer" title={att.fileName} style={style} onClick={onClick}>
        {children}
      </a>
    );
  }
  if (pending) {
    return (
      <span title={att.fileName} style={style}>
        {children}
      </span>
    );
  }
  return (
    <span
      role="button"
      tabIndex={0}
      title={att.fileName}
      style={{ ...style, cursor: 'pointer' }}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(e as unknown as React.MouseEvent<HTMLElement>);
        }
      }}
    >
      {children}
    </span>
  );
}

const styles = {
  single: { marginTop: 8 } satisfies React.CSSProperties,
  singleLink: (palette: AttachmentPalette, isImage: boolean): React.CSSProperties => ({
    // An image wrapper shrink-wraps its thumbnail (inline-block) — NOT a flex row: as a flex
    // item the <img> takes its main-axis width from the intrinsic width while `max-height` clamps
    // the cross axis independently, which breaks the aspect ratio and squishes the photo.
    display: isImage ? 'inline-block' : 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: isImage ? 0 : '6px 10px',
    background: palette.tileBg,
    border: palette.tileBorder,
    borderRadius: 'var(--sym-radius-badge)',
    color: palette.fg,
    fontSize: '0.75rem',
    textDecoration: 'none',
    maxWidth: isImage ? PICTURE_MAX_W : 220,
    overflow: 'hidden',
    // Top-aligned so the inline-block adds no descender gap under a picture; the game above it is a block.
    verticalAlign: 'top',
  }),
  // width/height auto + both max caps → the browser scales the image to fit the box while
  // preserving its natural aspect ratio (portrait renders tall, landscape wide — never cropped).
  //
  // The WIDTH rule is the document card's (`InlineAttachmentPreview` — the bubble's width), because a photo
  // and a document card are the same object to a reader: an image rendered narrower than the card above it
  // reads as a different kind of thing. Reported on the phones, and true here for the same reason. The
  // height cap is the document preview's, so neither can outgrow the other.
  singleImage: {
    display: 'block',
    maxWidth: '100%',
    maxHeight: PICTURE_MAX_H,
    width: 'auto',
    height: 'auto',
    objectFit: 'contain',
    borderRadius: 8,
  } satisfies React.CSSProperties,
  label: { display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, overflow: 'hidden' } satisfies React.CSSProperties,
  labelName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: 'var(--sym-weight-medium)',
  } satisfies React.CSSProperties,
  labelMeta: {
    fontSize: '0.6875rem',
    lineHeight: '0.875rem',
    color: 'rgba(var(--ink-rgb), 0.55)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } satisfies React.CSSProperties,
  list: { marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 } satisfies React.CSSProperties,
  tileColumn: { display: 'flex', flexDirection: 'column', alignItems: 'center', width: TILE } satisfies React.CSSProperties,
  tileLink: (palette: AttachmentPalette): React.CSSProperties => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    width: TILE,
    color: palette.fg,
    fontSize: '0.6875rem',
    textDecoration: 'none',
  }),
  tileWell: (palette: AttachmentPalette): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: TILE,
    height: TILE,
    background: palette.tileBg,
    border: palette.tileBorder,
    borderRadius: 'var(--sym-radius-badge)',
    overflow: 'hidden',
  }),
  // Cover, not contain: at this size a letterboxed photo reads as a smaller box inside a box.
  tileImage: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' } satisfies React.CSSProperties,
  generatingFill: { display: 'block', width: '100%', height: '100%' } satisfies React.CSSProperties,
  // The box a single picture arrives in: sized inline, from the placeholder's footprint to the picture's
  // own shape, on the same clock as the picture's fade.
  arrivingPicture: {
    position: 'relative',
    display: 'block',
    overflow: 'hidden',
    borderRadius: 8,
    transition: `width ${ARRIVE_MS}ms cubic-bezier(0.2, 0.8, 0.2, 1), height ${ARRIVE_MS}ms cubic-bezier(0.2, 0.8, 0.2, 1)`,
  } satisfies React.CSSProperties,
  arrivingFade: { transition: `opacity ${ARRIVE_MS}ms ease-out` } satisfies React.CSSProperties,
  arrivingTile: { position: 'relative', display: 'block', width: '100%', height: '100%', overflow: 'hidden' } satisfies React.CSSProperties,
  arrivingUnderlay: { position: 'absolute', inset: 0, display: 'block' } satisfies React.CSSProperties,
  arrivingImage: { position: 'absolute', inset: 0, display: 'block', width: '100%', height: '100%', objectFit: 'cover' } satisfies React.CSSProperties,
  // A settled picture's frame: shrink-wraps the image, and anchors the credits badge over it.
  arrivedNaturalBox: { position: 'relative', display: 'block', borderRadius: 8, overflow: 'hidden' } satisfies React.CSSProperties,
  // The chip-shaped placeholder: the graphic's own footprint, so the row is the height it will be once
  // the file arrives and the name is readable the whole time it is waiting.
  generatingGlyph: (palette: AttachmentPalette): React.CSSProperties => ({
    position: 'relative',
    display: 'block',
    width: GRAPHIC_H,
    height: GRAPHIC_H,
    flexShrink: 0,
    borderRadius: 6,
    overflow: 'hidden',
    background: palette.tileBg,
  }),
  generatingName: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } satisfies React.CSSProperties,
  // The space the picture is announced in; `ArrivingPicture` grows it to the picture's shape on arrival.
  generatingSingle: (palette: AttachmentPalette): React.CSSProperties => ({
    display: 'block',
    width: RESERVED_IMAGE,
    height: RESERVED_IMAGE,
    overflow: 'hidden',
    background: palette.tileBg,
    border: palette.tileBorder,
    borderRadius: 8,
  }),
  // Laid over the shimmer, which keeps running: a picture this late is usually a slow render, not a dead one,
  // and nothing on the client can tell the two apart.
  stalledText: {
    position: 'absolute',
    inset: 0,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 8px',
    textAlign: 'center',
  } satisfies React.CSSProperties,
  // On a solid pill so the line stays readable wherever the shimmer is brightest.
  stalledPill: {
    padding: '5px 10px',
    borderRadius: 999,
    background: 'var(--ds-surface-sunken)',
    color: 'var(--ds-text-secondary)',
    fontSize: '0.75rem',
    lineHeight: 1.4,
  } satisfies React.CSSProperties,
  tileName: {
    width: '100%',
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: '0.875rem',
  } satisfies React.CSSProperties,
  tileNote: {
    width: '100%',
    textAlign: 'center',
    color: 'var(--ds-text-secondary)',
    fontSize: '0.6875rem',
    lineHeight: '0.875rem',
  } satisfies React.CSSProperties,
  brokenGlyph: { color: 'var(--ds-text-secondary)', flexShrink: 0 } satisfies React.CSSProperties,
  // Laid out inside `generatingSingle`'s footprint, so a single row keeps the height it had while waiting.
  brokenSingle: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '0 12px',
    boxSizing: 'border-box',
    textAlign: 'center',
  } satisfies React.CSSProperties,
  brokenSingleBox: { width: RESERVED_IMAGE, height: RESERVED_IMAGE, cursor: 'pointer' } satisfies React.CSSProperties,
  brokenTile: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    cursor: 'pointer',
  } satisfies React.CSSProperties,
  brokenName: {
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '0.75rem',
    color: 'var(--ds-text-primary)',
  } satisfies React.CSSProperties,
  brokenLine: { fontSize: '0.75rem', lineHeight: 1.4, color: 'var(--ds-text-secondary)' } satisfies React.CSSProperties,
  tileError: {
    width: '100%',
    textAlign: 'center',
    color: 'var(--ds-text-destructive)',
    fontSize: '0.6875rem',
    lineHeight: '0.875rem',
  } satisfies React.CSSProperties,
};
